// 데스크톱(Tauri) 전용 서버 URL 프록시.
// 번들 프론트는 tauri.localhost(또는 tauri://localhost) origin 으로 로드되므로,
// 코드 곳곳의 상대 서버경로(/api, /avatar, /ufs, /file-upload, /websocket)가 서버에 닿지 못한다.
// RC_ORIGIN(빌드 시 VITE_TPT_RC_URL) 이 설정돼 있으면 fetch/WebSocket 을 절대 서버 URL 로 자동 프리픽스한다.
// 웹(같은 origin, RC_ORIGIN='') 에서는 아무 것도 하지 않는다(완전 무동작).
import { RC_ORIGIN } from '../api/rcClient';
import { diagLog } from './diag';
import { IS_DESKTOP_BETA } from '../utils/channel';

// 서버로 보내야 하는 절대경로 패턴. SPA 라우트(/, /login 등)나 정적 자산(/assets)은 제외.
const SERVER_PATH = /^\/(api|avatar|ufs|file-upload|websocket)(?:\/|\?|$)/;

export function installServerProxy(): void {
	if (!RC_ORIGIN) { diagLog('serverProxy OFF (relative/web)'); return; } // 웹: 상대경로 그대로 사용
	const httpOrigin = RC_ORIGIN.replace(/\/+$/, '');
	const wsOrigin = httpOrigin.replace(/^http/, 'ws');
	diagLog('serverProxy ON →', httpOrigin);

	// 🧪 데스크톱 베타(TPTalk Beta) — 교차 출처라 베타 쿠키가 안 실린다. 사이드카(/api/tpt/*) 요청에만 X-TPT-Channel 을 달아
	//   nginx 가 (사내 IP 일 때) 베타 사이드카로 보내게 한다. RC(/api/v1) 는 CORS 허용 헤더가 달라 붙이지 않는다. 정식 앱은 BETA_TPT=null → 무변화.
	const BETA_TPT = IS_DESKTOP_BETA ? httpOrigin + '/api/tpt/' : null;
	const tag = (url: string, init?: RequestInit): RequestInit | undefined => {
		if (!BETA_TPT || !url.startsWith(BETA_TPT)) return init;
		const h = new Headers(init?.headers);
		h.set('X-TPT-Channel', 'beta');
		return { ...init, headers: h };
	};

	// fetch — 상대 서버경로를 절대 서버 URL 로
	const origFetch = window.fetch.bind(window);
	window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
		try {
			if (typeof input === 'string' && SERVER_PATH.test(input)) {
				return origFetch(httpOrigin + input, tag(httpOrigin + input, init));
			}
			if (input instanceof URL && input.origin === window.location.origin && SERVER_PATH.test(input.pathname)) {
				return origFetch(httpOrigin + input.pathname + input.search, tag(httpOrigin + input.pathname, init));
			}
			if (BETA_TPT && (typeof input === 'string' || input instanceof URL) && String(input).startsWith(BETA_TPT)) {
				return origFetch(input, tag(String(input), init));
			}
		} catch { /* fallthrough to original */ }
		return origFetch(input as RequestInfo, init);
	}) as typeof window.fetch;

	// WebSocket — 상대 '/websocket' 등을 절대 ws URL 로 (rcRealtime 은 envUrl 로 이미 절대지만 안전망)
	const OrigWS = window.WebSocket;
	class PatchedWebSocket extends OrigWS {
		constructor(url: string | URL, protocols?: string | string[]) {
			let u: string | URL = url;
			if (typeof u === 'string' && u.startsWith('/') && SERVER_PATH.test(u)) u = wsOrigin + u;
			super(u, protocols);
		}
	}
	window.WebSocket = PatchedWebSocket as unknown as typeof WebSocket;
}
